import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button, OfflineState, useToast } from "@/ui";
import { useT, useUiStore } from "@/app";
import { useCan } from "@/auth";
import { token } from "@/design/theme";
import { PageHeader } from "../components/PageHeader";
import { IdentiteForm } from "../components/IdentiteForm";
import { DedupPanel } from "../components/DedupPanel";
import { ForbiddenInline } from "../components/ForbiddenInline";
import { useDossierSearch, useRegisterDossier, suggestByNin } from "../lib/api";
import { useDebounced } from "../lib/useDebounced";
import { CAPS } from "../lib/caps";
import type { Patient, RegisterDossierBody, SuggestNinResult } from "../lib/types";

/**
 * DossierIntakePage — patient+dossier intake with LIVE dedup. As the operator types nom/téléphone we
 * run the intra-lab search (§4.3); on NIN blur we run the cross-lab suggest (R-11, dark day-1). The
 * mandatory identity set (D-13) is required before submit. On success → the patient's demande
 * workspace. 5 states: forbidden (no create cap), offline, plus the form's own loading/error.
 */
export function DossierIntakePage() {
  const { t } = useT();
  const navigate = useNavigate();
  const toast = useToast();
  const online = useUiStore((s) => s.online);
  const canCreate = useCan(CAPS.dossierCreate);

  // Live dedup signal (name + phone), debounced into the search query.
  const [dedupSignal, setDedupSignal] = useState({ nom: "", telephone: "" });
  const dedupQuery = useDebounced(
    (dedupSignal.telephone || dedupSignal.nom).trim(),
    350,
  );
  const search = useDossierSearch(dedupQuery);

  const [ninResult, setNinResult] = useState<SuggestNinResult | null>(null);
  const [ninChecking, setNinChecking] = useState(false);

  const register = useRegisterDossier();

  if (!canCreate) {
    return (
      <div style={{ display: "flex", flexDirection: "column", gap: token.s5 }}>
        <PageHeader title={t("reception.intake.title")} />
        <ForbiddenInline requiredCapabilities={[CAPS.dossierCreate]} />
      </div>
    );
  }

  const onNinBlur = (nin: string) => {
    setNinChecking(true);
    suggestByNin(nin)
      .then((r) => setNinResult(r))
      .catch(() => setNinResult(null))
      .finally(() => setNinChecking(false));
  };

  const onSubmit = (body: RegisterDossierBody) => {
    register.mutate(body, {
      onSuccess: (res) => {
        toast.success(t("reception.toast.dossierCreated"));
        navigate(
          `/reception/demandes/nouvelle?dossier=${res.dossier.id}&patient=${res.patient.id}&nom=${encodeURIComponent(
            `${res.patient.nom} ${res.patient.prenom}`,
          )}`,
        );
      },
      onError: (err) => toast.error(err.message || t("reception.toast.dossierError")),
    });
  };

  const useExisting = (p: Patient) => {
    if (!p.dossier) return;
    navigate(
      `/reception/demandes/nouvelle?dossier=${p.dossier.id}&patient=${p.id}&nom=${encodeURIComponent(
        `${p.nom} ${p.prenom}`,
      )}`,
    );
  };

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: token.s5 }}>
      <PageHeader
        title={t("reception.intake.title")}
        actions={
          <Button variant="ghost" onClick={() => navigate("/reception")}>
            {t("reception.back")}
          </Button>
        }
      />

      {!online ? (
        <OfflineState />
      ) : (
        <IdentiteForm
          saving={register.isPending}
          onSubmit={onSubmit}
          onCancel={() => navigate("/reception")}
          onDedupSignalChange={setDedupSignal}
          onNinBlur={onNinBlur}
          dedupSlot={
            <DedupPanel
              matches={dedupQuery.length >= 2 ? search.data ?? [] : []}
              searching={dedupQuery.length >= 2 && search.isFetching}
              ninResult={ninResult}
              ninChecking={ninChecking}
              onUseExisting={useExisting}
            />
          }
        />
      )}
    </div>
  );
}
